import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { FileUploadComponent } from '@ui/file-upload/file-upload.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { forkJoin } from 'rxjs';
import { LoadingService } from '@ui/app-layout/loading-service';
import { NotificationService } from '@ui/notification/notification.service';

const MAX_BYTES = 25600 * 1024; // Laravel max:25600 (KB) voor dit endpoint
const ACCEPT =
  '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx';

@Component({
  selector: 'app-upload-municipality-document-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, ReactiveFormsModule, FileUploadComponent],
  template: `
    <ui-modal
      [title]="'Document uploaden'"
      [submitText]="'Uploaden'"
      [cancelText]="'Annuleren'"
      (close)="closeModal()"
      (save)="saveModal()"
    >
      <form [formGroup]="uploadForm" class="space-y-6">
        <ui-file-upload
          formControlName="files"
          [multiple]="true"
          [accept]="accept"
          [maxFileSize]="maxFileSize"
          [helperText]="helperText"
        ></ui-file-upload>
      </form>
    </ui-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadMunicipalityDocumentModalComponent {
  @Input() municipalityId!: string;

  private fb = inject(FormBuilder);
  private modalRef = inject(MODAL_REF);
  private modalService = inject(ModalService);
  private municipalitiesService = inject(MunicipalitiesService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);

  readonly accept = ACCEPT;
  readonly maxFileSize = MAX_BYTES;
  readonly helperText =
    'PDF, Word, Excel, afbeeldingen of tekst (max. ca. 25 MB per bestand). Meerdere bestanden: elk wordt apart geüpload.';

  uploadForm = this.fb.group({
    files: [[] as File[]],
  });

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<unknown>);
  }

  saveModal() {
    const files = this.uploadForm.get('files')?.value as File[] | undefined;
    if (!files?.length || !this.municipalityId) {
      return;
    }

    this.loadingService.show();
    forkJoin(
      files.map((file) =>
        this.municipalitiesService.uploadDocument(this.municipalityId, file)
      )
    ).subscribe({
      next: () => {
        this.loadingService.hide();
        this.notificationService.success('Document(en) geüpload.');
        this.modalService.close(this.modalRef as ModalRef<unknown>);
      },
      error: () => {
        this.loadingService.hide();
        this.notificationService.error(
          'Upload mislukt. Controleer bestandstype en grootte, of uw rechten.'
        );
      },
    });
  }
}
